// BothVisitForm — modal to log a single "Both Direct Visit".
// Creates a Buyer (site) visit AND a Seller (self) visit from one submission,
// sharing Property / Date / Status. Lead + Interest + Feedback are buyer-only,
// Remark is seller-only. Visit type is always "direct" (no agent toggle).

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, Save, Search, ChevronDown } from "lucide-react";
import { fromDateTimeLocal } from "../../utils/datetime";
import "../../css/properties/propertyform.css";

/* Reusable searchable combobox (mirrors SiteVisitForm / SelfVisitForm) */
function SearchableSelect({ value, onChange, options, placeholder = "— Select —", displayKey = "label", valueKey = "value" }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((o) =>
    String(o[displayKey]).toLowerCase().includes(query.toLowerCase())
  );
  const selectedLabel = options.find((o) => o[valueKey] === value)?.[displayKey] || "";

  const handleSelect = (val) => { onChange(val); setOpen(false); setQuery(""); };

  return (
    <div className="searchable-select" ref={ref}>
      <div className={`ss-trigger ${open ? "open" : ""}`} onClick={() => setOpen((p) => !p)}>
        <span className={selectedLabel ? "ss-value" : "ss-placeholder"}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown size={14} className={`ss-chevron ${open ? "rotated" : ""}`} />
      </div>

      {open && (
        <div className="ss-dropdown">
          <div className="ss-search">
            <Search size={13} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <ul className="ss-list">
            {filtered.length === 0 && <li className="ss-empty">No results</li>}
            {filtered.map((o) => (
              <li
                key={o[valueKey]}
                className={`ss-item ${o[valueKey] === value ? "selected" : ""}`}
                onClick={() => handleSelect(o[valueKey])}
              >
                {o[displayKey]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  lead_id:         "",
  property_id:     "",
  scheduled_date:  "",
  status:          "scheduled",
  feedback:        "",         // shown only when status === "completed"
  remark:          "",         // shown for any other status
  seller_name:     "",
  seller_phone:    "",
  seller_email:    "",
};

export default function BothVisitForm({ leads, properties, onSubmit, onCancel, isLoading }) {
  const [formData, setFormData] = useState(EMPTY_FORM);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isCompleted = formData.status === "completed";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.lead_id || !formData.property_id || !formData.scheduled_date) {
      alert("Lead, Property and Visit Date & Time are required");
      return;
    }
    onSubmit({
      ...formData,
      visit_type:      "direct",
      scheduled_date:  fromDateTimeLocal(formData.scheduled_date),
      // Keep only the field relevant to the current status
      feedback: isCompleted ? formData.feedback : "",
      remark:   isCompleted ? "" : formData.remark,
    });
  };

  const leadOptions     = leads.map((l) => ({ value: l._id, label: l.full_name + (l.phone ? ` — ${l.phone}` : "") }));
  const propertyOptions = properties.map((p) => ({ value: p._id, label: p.propertyName + (p.location ? `, ${p.location}` : "") }));

  return (
    <motion.div
      className="property-form-overlay"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="property-form-card">
        {/* HEADER */}
        <div className="form-header">
          <h2>Both Visits (Buyer + Seller)</h2>
          <button className="icon-btn" type="button" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* LEAD & PROPERTY — searchable */}
          <div className="grid-2">
            <div className="form-group">
              <label>Lead *</label>
              <SearchableSelect
                value={formData.lead_id}
                onChange={(val) => setFormData((prev) => ({ ...prev, lead_id: val }))}
                options={leadOptions}
                placeholder="— Select Lead —"
              />
            </div>

            <div className="form-group">
              <label>Property *</label>
              <SearchableSelect
                value={formData.property_id}
                onChange={(val) => setFormData((prev) => ({ ...prev, property_id: val }))}
                options={propertyOptions}
                placeholder="— Select Property —"
              />
            </div>
          </div>

          {/* DATE & STATUS */}
          <div className="grid-2">
            <div className="form-group">
              <label>Visit Date & Time *</label>
              <input
                type="datetime-local"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>
          </div>

          {/* SELLER DETAILS (seller side) */}
          <div className="grid-2">
            <div className="form-group">
              <label>Seller Name</label>
              <input
                type="text"
                name="seller_name"
                placeholder="Seller full name"
                value={formData.seller_name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Seller Phone</label>
              <input
                type="tel"
                name="seller_phone"
                placeholder="Seller phone number"
                value={formData.seller_phone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Seller Email</label>
            <input
              type="email"
              name="seller_email"
              placeholder="seller@example.com"
              value={formData.seller_email}
              onChange={handleChange}
            />
          </div>

          {/* FEEDBACK when completed, otherwise REMARK */}
          {isCompleted ? (
            <div className="form-group">
              <label>Feedback</label>
              <textarea
                rows="3"
                name="feedback"
                placeholder="Enter feedback about the completed visit..."
                value={formData.feedback}
                onChange={handleChange}
              />
            </div>
          ) : (
            <div className="form-group">
              <label>Remarks</label>
              <textarea
                rows="3"
                name="remark"
                placeholder="Enter remarks about this visit..."
                value={formData.remark}
                onChange={handleChange}
              />
            </div>
          )}

          {/* FOOTER */}
          <div className="form-footer">
            <button type="button" onClick={onCancel} className="btn-outline">Cancel</button>
            <button type="submit" disabled={isLoading} className="btn-primary">
              <Save size={16} /> {isLoading ? "Saving..." : "Save Both Visits"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
